import React, { useEffect, useMemo, useState } from "react";
import { useCollection } from "api/hooks/useCollection";
import { Track } from "api/model/Track";
import { Collections } from "api/model/Collections";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
} from "@material-ui/core";
import { useFormikContext } from "formik";
import { useFieldValueAndValidation } from "ui/componentes/Form/functions";
import MaterialTable from "material-table";
import { Search } from "@material-ui/icons";
import { LocalizationPTBr } from "util/MaterialTableUtil";

interface SelectTrackProps {
    selected?: Track;
    name: string;
    helperText?: string;
}

const SelectTrackDialog = ({ tracks, onSelectTrack }) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            <IconButton onClick={() => setOpen(true)} style={{ padding: 0 }}>
                <Search />
            </IconButton>
            <Dialog
                open={open}
                disableEscapeKeyDown
                disableBackdropClick
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Selecionar pista</DialogTitle>
                <DialogContent dividers>
                    <MaterialTable
                        components={{ Container: Box }}
                        columns={[
                            {
                                field: "name",
                                title: "Nome",
                            },
                            {
                                field: "friendlyName",
                                title: "Nome comercial",
                            },
                        ]}
                        data={tracks}
                        options={{
                            toolbar: false,
                            filtering: true,
                            draggable: false,
                            search: false,
                            showTitle: false,
                        }}
                        onRowClick={(e, track) => {
                            onSelectTrack(track);
                            setOpen(false);
                        }}
                        localization={LocalizationPTBr}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setOpen(false);
                        }}
                    >
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const SelectTrack: React.FC<SelectTrackProps> = ({ name, helperText }) => {
    const { setFieldValue } = useFormikContext<any>();
    const { error, hasTouched, value: formValue } = useFieldValueAndValidation(
        name
    );
    const showError = !!error && hasTouched;

    const tracks = useCollection<Track[]>(
        { collection: Collections.TRACKS },
        true
    );
    const tracksById = useMemo(() => {
        const tracksById: { [id: string]: Track } = {};
        tracks.forEach((track) => (tracksById[track.id] = track));
        return tracksById;
    }, [tracks]);

    const selectedTrack = useMemo(() => tracksById[formValue], [
        tracksById,
        formValue,
    ]);

    useEffect(() => {
        tracks.sort((track1, track2) =>
            track1.friendlyName < track2.friendlyName ? -1 : 1
        );
    }, [tracks]);

    return (
        <Box display="flex">
            <TextField
                style={{ flexGrow: 1 }}
                label="Pista"
                helperText={showError ? error : helperText}
                disabled
                value={selectedTrack?.friendlyName || ""}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <SelectTrackDialog
                                tracks={tracks}
                                onSelectTrack={(track) =>
                                    setFieldValue(name, track?.id || "")
                                }
                            />
                        </InputAdornment>
                    ),
                }}
            />
        </Box>
    );
};

export { SelectTrack };
